import { classicMap } from "./classic.js";
import { meltdownMap } from "./meltdown.js";
import { gauntletMap } from "./gauntlet.js";
import {
  isCustomMapId,
  isCommunityMapId,
  getCustomMap,
  buildRuntimeMap,
} from "../customMaps.js";

// The map registry. A "map" is a named bundle of templates that plugs into the
// same distance-driven difficulty progression — see ./_helpers.js for the shape
// and docs/level-generation.md for the bigger picture. Selected via ?map=<id>.
//
// Built-in maps are static (bundled at build time). Player-authored "custom"
// levels (id prefixed "custom-") live in localStorage and are resolved
// dynamically — see ../customMaps.js.

// Order here is the order maps appear in the splash-screen picker UI.
const MAP_LIST = [classicMap, meltdownMap, gauntletMap];

// Classic reproduces the original behavior, so it's the default when no (or an
// unknown) map id is supplied — existing links and the bare URL are unaffected.
export const DEFAULT_MAP_ID = classicMap.id;

const MAPS_BY_ID = MAP_LIST.reduce((acc, map) => {
  acc[map.id] = map;
  return acc;
}, {});

// Custom maps are validated/built on first access and cached for the page's
// lifetime. The page reloads whenever the active map changes (see main.js), so
// a cached build never goes stale within a single game/editor session.
const customRuntimeCache = new Map();

// Runtime-registered maps that aren't in localStorage: other players' shared
// community levels (id "<username>/<slug>") AND published levels (id = the
// published-version UUID, used as the leaderboard map id). main.js fetches the
// content and registers a pre-built runtime map here BEFORE the game is built,
// keeping the synchronous getMap() path intact. Keyed by whatever id main.js
// passes (a "<username>/<slug>" ref or a bare version uuid).
const communityRuntimeCache = new Map();

/**
 * Register a pre-built runtime map so the synchronous getMap()/resolveMapId path
 * can serve it. Called by main.js after fetching the level by its share ref.
 * @param {string} id  the runtime map id (community "<username>/<slug>" OR a
 *   published-version uuid)
 * @param {Object} runtimeMap  a built designed-map (from buildRuntimeMap)
 */
export function registerCommunityMap(id, runtimeMap) {
  if (id && runtimeMap) communityRuntimeCache.set(id, runtimeMap);
}

/**
 * Normalize a (possibly null/unknown) map id to a real one, falling back to
 * the default. Keeps unknown ?map= values from breaking the game.
 * @param {string|null|undefined} id
 * @returns {string}
 */
export function resolveMapId(id) {
  // A runtime-registered map (community ref OR published-version uuid) wins.
  if (id && communityRuntimeCache.has(id)) return id;
  if (isCustomMapId(id) && getCustomMap(id)) return id;
  return id && MAPS_BY_ID[id] ? id : DEFAULT_MAP_ID;
}

/**
 * Get a map definition by id, falling back to the default for unknown ids.
 * Custom (localStorage) levels are built into a runtime designed-map on first
 * access; a malformed one falls back to the default so a bad value never breaks
 * boot.
 * @param {string|null|undefined} id
 * @returns {Object} the resolved map definition
 */
export function getMap(id) {
  // Runtime-registered (community/published) maps must have been registered before
  // now (main.js fetches + registers before building the game).
  if (id && communityRuntimeCache.has(id)) {
    return communityRuntimeCache.get(id);
  }
  if (isCommunityMapId(id)) {
    // A "<username>/<slug>" id that wasn't registered (race / typo) falls back to
    // the default map so the synchronous boot path never throws.
    return MAPS_BY_ID[DEFAULT_MAP_ID];
  }
  if (isCustomMapId(id)) {
    if (customRuntimeCache.has(id)) return customRuntimeCache.get(id);
    const stored = getCustomMap(id);
    if (stored) {
      const { map, error } = buildRuntimeMap(stored);
      if (map) {
        customRuntimeCache.set(id, map);
        return map;
      }
      console.warn(
        `getMap: custom map "${id}" failed to build (${error}); falling back to default.`
      );
    }
    return MAPS_BY_ID[DEFAULT_MAP_ID];
  }
  return MAPS_BY_ID[resolveMapId(id)];
}

/**
 * All registered maps, in display order. Handy for a future map-select menu.
 * @returns {Object[]}
 */
export function listMaps() {
  return [...MAP_LIST];
}
